import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdAccountTree,
  MdAnalytics,
  MdCampaign,
  MdClose,
  MdDashboard,
  MdFactory,
  MdFolder,
  MdForum,
  MdGroup,
  MdHomeFilled,
  MdLogout,
  MdMenuOpen,
  MdOutlineDarkMode,
  MdOutlineLightMode,
  MdPeople,
  MdPrecisionManufacturing,
  MdSettings,
  MdSwapHoriz,
  MdTerminal,
  MdTune,
  MdUploadFile,
  MdWhatsapp,
} from 'react-icons/md';
import { useUser } from '../../../../contexts/userContext';
import { useWhatsappZapi } from '../../../../contexts/whatsappZapiContext';
import {
  WHATSAPP_META_UI_ENABLED,
  isWhatsappMetaReviewerUser,
  isWhatsappZapiMenuHiddenForUser,
} from '../../../../config/whatsappMetaFeature';
import api from '../../../../services/api';
import { HOME_NEW_STORAGE, formatCurrency } from '../utils';
import { INTERFACE_LAYOUT } from '../../../../utils/interfaceLayout';
import NewAvatar from './NewAvatar';

function NewSidebar({
  user,
  getAccessLevel,
  theme,
  onThemeChange,
  themeSaving,
  onLogout,
  showValues,
  activePath = '/home-new',
  mobileOpen = false,
  onMobileClose = () => {},
}) {
  const navigate = useNavigate();
  const {
    userAvatar,
    setUserAvatar,
    setOpenCloseModalAvatar,
    setOpenCloseImportExcelSuiteFlow,
    updateUser,
  } = useUser();
  const { metaEnabled } = useWhatsappZapi();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(HOME_NEW_STORAGE.sidebar) === 'collapsed');
  const [profileOpen, setProfileOpen] = useState(false);
  const [sales, setSales] = useState({ individual: 0, team: 0 });
  const [layoutSaving, setLayoutSaving] = useState(false);
  const [layoutError, setLayoutError] = useState('');

  const navigation = useMemo(() => [
    { id: 'crm', label: 'CRM', icon: MdHomeFilled, path: '/home-new', visible: true },
    { id: 'dashboard', label: 'Dashboard', icon: MdDashboard, path: '/dashboard', visible: getAccessLevel('dashboard') },
    { id: 'pcp', label: 'PCP', icon: MdFactory, path: '/pcp', visible: true },
    { id: 'participante', label: 'Participante', icon: MdPeople, path: '/participantes', visible: true },
    { id: 'bazze-broker', label: 'Bazze Broker', icon: MdAccountTree, path: '/bazze-broker', visible: !isWhatsappMetaReviewerUser(user?.id) },
    { id: 'usuarios', label: 'Usuários', icon: MdGroup, path: '/users', visible: getAccessLevel('superadmin') },
    { id: 'configuracoes', label: 'Configurações', icon: MdSettings, path: '/process', visible: getAccessLevel('superadmin') },
    { id: 'pre-venda', label: 'Pré-venda', icon: MdTune, path: '/configuracoes/inteligencia-pre-venda', visible: [5, 8].includes(Number(user?.access_level)) },
    { id: 'whatsapp-zapi', label: 'WhatsApp Z-API', icon: MdWhatsapp, path: '/whatsapp-zapi', visible: getAccessLevel('adm') && !isWhatsappZapiMenuHiddenForUser(user?.id) },
    { id: 'whatsapp-oficial', label: 'WhatsApp Oficial', icon: MdForum, path: '/whatsapp-meta', visible: WHATSAPP_META_UI_ENABLED && metaEnabled },
    { id: 'import-suite-flow', label: 'Import SuiteFlow', icon: MdUploadFile, action: () => setOpenCloseImportExcelSuiteFlow(true), visible: getAccessLevel('adm100') },
    { id: 'terminal', label: 'Terminal', icon: MdTerminal, path: '/terminal', visible: true },
    { id: 'tv-producao', label: 'TV Produção', icon: MdPrecisionManufacturing, path: '/tvproducao', visible: true },
    { id: 'projetos', label: 'Projetos', icon: MdFolder, path: '/projetos', visible: getAccessLevel('adm100') },
    { id: 'marketing', label: 'Marketing', icon: MdCampaign, path: '/marketing', visible: getAccessLevel('adm100') },
    { id: 'painel-mkt', label: 'Painel MKT', icon: MdAnalytics, path: '/painel-mkt', visible: true },
  ].filter((item) => item.visible), [getAccessLevel, metaEnabled, setOpenCloseImportExcelSuiteFlow, user?.access_level, user?.id]);

  const toggleSidebar = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem(HOME_NEW_STORAGE.sidebar, next ? 'collapsed' : 'expanded');
  };

  useEffect(() => {
    if (user?.avatar) setUserAvatar(user.avatar);
  }, [setUserAvatar, user?.avatar]);

  useEffect(() => {
    if (!profileOpen || !user?.id) return undefined;
    let active = true;
    Promise.allSettled([
      api.get(`/card/sales/total/${user.id}`),
      api.get(`/card/sales/total-afilhados/${user.id}`),
    ]).then(([individual, team]) => {
      if (!active) return;
      setSales({
        individual: individual.status === 'fulfilled' ? Number(individual.value.data || 0) : 0,
        team: team.status === 'fulfilled' ? Number(team.value.data || 0) : 0,
      });
    });
    return () => { active = false; };
  }, [profileOpen, user?.id]);

  const individualTarget = Number(user?.meta_user || 0);
  const teamTarget = Number(user?.meta_grupo || 0);
  const individualProgress = individualTarget > 0 ? Math.min(100, (sales.individual / individualTarget) * 100) : 0;
  const teamProgress = teamTarget > 0 ? Math.min(100, (sales.team / teamTarget) * 100) : 0;
  const visuallyCollapsed = collapsed && !mobileOpen;

  const activateNavigation = (path, action) => {
    onMobileClose();
    if (action) action();
    else navigate(path);
  };

  const useClassicLayout = async () => {
    if (!user?.id || layoutSaving) return;

    setLayoutSaving(true);
    setLayoutError('');
    try {
      await api.put(`/users/${user.id}`, { interface_layout: INTERFACE_LAYOUT.classic });
      updateUser({ interface_layout: INTERFACE_LAYOUT.classic });
      setProfileOpen(false);
      navigate('/home');
    } catch {
      setLayoutError('Não foi possível alterar o layout. Tente novamente.');
    } finally {
      setLayoutSaving(false);
    }
  };

  return (
    <>
      {mobileOpen && (
        <button
          type="button"
          className="new-home-mobile-navigation-scrim"
          onClick={onMobileClose}
          aria-label="Fechar menu de navegação"
        />
      )}
      <aside className={`new-home-sidebar ${visuallyCollapsed ? 'is-collapsed' : ''} ${mobileOpen ? 'new-home-sidebar--mobile-open' : ''}`} aria-label="Navegação principal">
      <div className="new-home-brand">
        <span className="new-home-brand-mark"><img src="/logo192.png" alt="SyncsCRM" /></span>
        {!visuallyCollapsed && <span className="new-home-brand-name">Syncs<span>CRM</span></span>}
        {!visuallyCollapsed && (
          <button className="new-home-icon-button new-home-sidebar-toggle" onClick={toggleSidebar} aria-label="Recolher barra lateral" title="Recolher barra lateral">
            <MdMenuOpen aria-hidden="true" />
          </button>
        )}
        <button className="new-home-icon-button new-home-mobile-navigation-close" type="button" onClick={onMobileClose} aria-label="Fechar menu de navegação" title="Fechar menu">
          <MdClose aria-hidden="true" />
        </button>
      </div>

      <nav className="new-home-nav">
        {visuallyCollapsed ? (
          <button className="new-home-nav-item new-home-sidebar-expand" onClick={toggleSidebar} aria-label="Expandir barra lateral" title="Expandir barra lateral">
            <MdMenuOpen aria-hidden="true" />
          </button>
        ) : (
          <span className="new-home-nav-section">Workspace</span>
        )}
        {navigation.map(({ id, label, icon: Icon, path, action, divider }) => (
          <React.Fragment key={id}>
            {divider && <span className="new-home-nav-divider" />}
            <button
              className={`new-home-nav-item ${path === activePath ? 'is-active' : ''}`}
              onClick={() => activateNavigation(path, action)}
              title={visuallyCollapsed ? label : undefined}
              aria-label={label}
              aria-current={path === activePath ? 'page' : undefined}
            >
              <Icon aria-hidden="true" />
              {!visuallyCollapsed && <span>{label}</span>}
            </button>
          </React.Fragment>
        ))}
      </nav>

      <div className="new-home-sidebar-footer">
        <button className="new-home-nav-item" onClick={onThemeChange} disabled={themeSaving} title={visuallyCollapsed ? `Usar tema ${theme === 'dark' ? 'claro' : 'escuro'}` : undefined} aria-label={`Usar tema ${theme === 'dark' ? 'claro' : 'escuro'}`}>
          {theme === 'dark' ? <MdOutlineLightMode /> : <MdOutlineDarkMode />}
          {!visuallyCollapsed && <span>{themeSaving ? 'Salvando tema…' : `Tema ${theme === 'dark' ? 'claro' : 'escuro'}`}</span>}
        </button>
        <div className="new-home-user-card">
          <button className="new-home-user-profile" onClick={() => setProfileOpen(true)} title="Abrir perfil">
            <span className="new-home-avatar"><NewAvatar avatar={userAvatar || user?.avatar} alt={`Avatar de ${user?.username || 'usuário'}`} /></span>
            {!visuallyCollapsed && (
              <span className="new-home-user-copy">
                <strong>{user?.username || 'Usuário'}</strong>
                <small>{user?.user_type || 'SyncsCRM'}</small>
              </span>
            )}
          </button>
          <button className="new-home-icon-button" onClick={onLogout} aria-label="Sair" title="Sair">
            <MdLogout />
          </button>
        </div>
      </div>

      {profileOpen && <button className="new-home-profile-scrim" aria-label="Fechar perfil" onClick={() => setProfileOpen(false)} />}
      {profileOpen && (
        <section className="new-home-profile-popover" aria-label="Perfil do usuário">
          <header>
            <button
              className="new-home-profile-avatar-button"
              onClick={() => { setProfileOpen(false); setOpenCloseModalAvatar(true); }}
              title="Alterar avatar"
            >
              <NewAvatar avatar={userAvatar || user?.avatar} alt={`Avatar de ${user?.username || 'usuário'}`} />
              <span>Alterar avatar</span>
            </button>
            <div><strong>{user?.username || 'Usuário'}</strong><small>{user?.user_type || 'SyncsCRM'}</small></div>
          </header>
          <div className="new-home-profile-metric">
            <div><span>Minha meta</span><b>{Math.round(individualProgress)}%</b></div>
            <div className="new-home-profile-progress"><i style={{ width: `${individualProgress}%` }} /></div>
            <small>{showValues ? `${formatCurrency(sales.individual)} de ${formatCurrency(individualTarget)}` : '•••••• de ••••••'}</small>
          </div>
          <div className="new-home-profile-metric">
            <div><span>Meta da equipe</span><b>{Math.round(teamProgress)}%</b></div>
            <div className="new-home-profile-progress"><i style={{ width: `${teamProgress}%` }} /></div>
            <small>{showValues ? `${formatCurrency(sales.team)} de ${formatCurrency(teamTarget)}` : '•••••• de ••••••'}</small>
          </div>
          <button className="new-home-profile-layout" onClick={useClassicLayout} disabled={layoutSaving}><MdSwapHoriz />{layoutSaving ? 'Alterando…' : 'Usar layout clássico'}</button>
          {layoutError && <span className="new-home-profile-error">{layoutError}</span>}
          <button className="new-home-profile-logout" onClick={onLogout}><MdLogout />Sair</button>
        </section>
      )}
      </aside>
    </>
  );
}

export default React.memo(NewSidebar);
